// =============================================================================
// CompanyComplianceScreen — /company-compliance  (T-388)
// The company's internal compliance hub. Generic register (item_type) with
// INSURANCE as the first citizen; licences / registrations / certifications
// slot in later with zero redesign. Dashboard-style: status cards with visual
// expiry, upcoming-renewals strip, one-click cert download ("proof of
// insurance in one click"), renew-with-history (supersede chain).
// =============================================================================

import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck, Plus, AlertTriangle, CalendarClock, FileText, Upload,
  Download, Eye, RefreshCw, History, Trash2, Banknote, Package,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState, KPI, Input, Field } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import StatutoryLodgementsCard from "./StatutoryLodgementsCard";   // 2 Nov 2026 — mig 492, the statutory lodgements register (directors)

const BUCKET = "ap-documents";

const ITEM_TYPES: { v: string; label: string }[] = [
  { v: "insurance",     label: "Insurance" },
  { v: "licence",       label: "Licence" },
  { v: "registration",  label: "Registration" },
  { v: "certification", label: "Certification" },
  { v: "regulatory",    label: "Regulatory" },
  { v: "other",         label: "Other" },
];

const INSURANCE_CATEGORIES: { v: string; label: string }[] = [
  { v: "business_package",       label: "Business package" },   // mig 353 — sectioned policy (Zurich pkg)
  { v: "public_liability",       label: "Public liability" },
  { v: "professional_indemnity", label: "Professional indemnity" },
  { v: "workers_comp",           label: "Workers comp / WorkCover" },
  { v: "vehicle",                label: "Vehicle / fleet" },
  { v: "tools_equipment",        label: "Tools & equipment" },
  { v: "cyber",                  label: "Cyber" },
  { v: "other",                  label: "Other" },
];

function daysTo(d: string | null): number | null {
  if (!d) return null;
  return Math.round((new Date(d + "T23:59:59").getTime() - Date.now()) / 86400000);
}
function expiryMeta(d: string | null): { tone: string; ring: string; label: string } {
  const n = daysTo(d);
  if (n == null)  return { tone: "text-text-muted", ring: "border-line",       label: "no expiry set" };
  if (n < 0)      return { tone: "text-danger",     ring: "border-danger/60",  label: `expired ${Math.abs(n)}d ago` };
  if (n <= 30)    return { tone: "text-warn",       ring: "border-warn/60",    label: `renews in ${n}d` };
  return           { tone: "text-ok",               ring: "border-line",       label: `renews ${fmtDate(d)}` };
}

// mig 353 — one row of a package policy's breakdown (Property / Burglary /
// General property / Glass, each with its own sum insured + excess).
interface CoverSection {
  label: string;
  sum_insured: number | null;
  excess: number | null;
  note: string | null;
}

interface Item {
  item_id: string; item_type: string; category: string | null; display_name: string;
  provider: string | null; broker: string | null; reference: string | null;
  cover_amount: number | null; excess_amount: number | null; premium_estimate: number | null;
  effective_from: string | null; expiry_date: string | null; details: string | null;
  document_path: string | null; document_name: string | null;
  cover_sections: CoverSection[] | null;   // mig 353 — null for single-cover policies
  is_active: boolean; superseded_by_item_id: string | null; created_at: string;
}

export default function CompanyComplianceScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canEdit = hasRole(user, "manager");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [editing, setEditing] = useState<Partial<Item> | null>(null);   // {} = new; with item_id = edit; _renewOf = renew
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["company_compliance"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_compliance_items")
        .select("*, docs:company_compliance_documents(*)")
        .order("expiry_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      // unknown-first: database.types.ts predates cover_sections (mig 353)
      return (data ?? []) as unknown as Item[];
    },
  });

  // mig 353 — nest the CapEx Insurance register into this hub (James, 4 Sep:
  // "put the insured items screen nav in this screen to nest it together").
  // Count of significant purchases still awaiting broker cover.
  const { data: assetCounts = { pending: 0, total: 0 } } = useQuery({
    queryKey: ["compliance_asset_counts"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("company_assets")
        .select("asset_id, insurance_status, insurance_required");
      if (error) { console.error("company_assets count:", error.message); return { pending: 0, total: 0 }; }
      const rows = (data ?? []) as { insurance_status: string; insurance_required: boolean }[];
      return {
        pending: rows.filter(r => r.insurance_status === "pending" && r.insurance_required).length,
        total: rows.length,
      };
    },
    staleTime: 60_000,
  });

  const active = useMemo(() => items.filter(i => i.is_active), [items]);
  const typesPresent = useMemo(() => [...new Set(active.map(i => i.item_type))], [active]);
  const shown = useMemo(
    () => active.filter(i => typeFilter === "all" || i.item_type === typeFilter),
    [active, typeFilter]);

  const kpis = useMemo(() => {
    const expiring = active.filter(i => { const n = daysTo(i.expiry_date); return n != null && n >= 0 && n <= 30; });
    const expired  = active.filter(i => { const n = daysTo(i.expiry_date); return n != null && n < 0; });
    const cover    = active.filter(i => i.item_type === "insurance")
      .reduce((a, i) => a + Number(i.cover_amount ?? 0), 0);
    return { total: active.length, expiring: expiring.length, expired: expired.length, cover };
  }, [active]);

  const upcoming = useMemo(
    () => active.filter(i => i.expiry_date).slice(0, 4),
    [active]);

  // History chain for an item: all archived rows whose supersede pointer walks
  // forward to it (client-side walk over the full list).
  function historyOf(itemId: string): Item[] {
    const out: Item[] = [];
    let cursor = itemId;
    for (let hop = 0; hop < 20; hop++) {
      const prev = items.find(i => i.superseded_by_item_id === cursor);
      if (!prev) break;
      out.push(prev); cursor = prev.item_id;
    }
    return out;
  }

  async function viewDoc(path: string) {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  }
  async function downloadDoc(item: Item) {
    if (!item.document_path) return;
    const { data, error } = await supabase.storage.from(BUCKET).download(item.document_path);
    if (error || !data) { window.alert("Couldn't download — try View."); return; }
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.document_name || `${item.display_name.replace(/[^\w]+/g, "_")}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }
  async function archive(item: Item) {
    if (!window.confirm(`Archive "${item.display_name}"? It moves to history (not deleted).`)) return;
    await supabase.from("company_compliance_items").update({ is_active: false }).eq("item_id", item.item_id);
    qc.invalidateQueries({ queryKey: ["company_compliance"] });
  }

  if (!user) return null;
  if (!canEdit) {
    return <div className="p-6 max-w-2xl mx-auto"><Card><EmptyState icon={ShieldCheck} title="Managers only" description="Company compliance is restricted to manager-and-above roles." /></Card></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto pb-20">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck size={20} className="text-accent" /> Company compliance
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            The company's own compliance — insurances first, everything renewable in one place. Proof of cover is one click away.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setEditing({ item_type: "insurance" })}>Add item</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Active items" value={kpis.total} icon={ShieldCheck} />
        <KPI label="Renewing ≤ 30 days" value={kpis.expiring} icon={CalendarClock} accent={kpis.expiring > 0} />
        <KPI label="Expired" value={kpis.expired} icon={AlertTriangle} accent={kpis.expired > 0} />
        <KPI label="Total insured cover" value={fmtMoney(kpis.cover)} icon={Banknote} />
      </div>

      {/* Insured items — the CapEx Insurance register, nested here (mig 353) */}
      <Link to="/assets/insurance-register" className="block mb-5 group">
        <div className={`rounded-md border px-4 py-3 flex items-center gap-3 bg-ink-card transition-colors group-hover:border-accent ${
          assetCounts.pending > 0 ? "border-warn/60" : "border-line"}`}>
          <Package size={16} className={assetCounts.pending > 0 ? "text-warn" : "text-accent"} />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-text">Insured items — significant purchases</div>
            <div className="text-[11px] text-text-muted">
              {assetCounts.pending > 0
                ? `${assetCounts.pending} purchase${assetCounts.pending === 1 ? "" : "s"} awaiting broker cover · ${assetCounts.total} on the register`
                : assetCounts.total > 0
                  ? `All ${assetCounts.total} registered items squared away with the broker`
                  : "CapEx purchases over the threshold land here from AP coding"}
            </div>
          </div>
          {assetCounts.pending > 0 && <Pill label={`${assetCounts.pending} pending`} tone="pending" small />}
          <span className="text-[11px] text-accent">Open register →</span>
        </div>
      </Link>

      {/* Statutory lodgements — BAS, IAS, payroll tax, income tax, ASIC, WorkCover (mig 492, directors only) */}
      <StatutoryLodgementsCard />

      {/* Upcoming renewals strip */}
      {upcoming.length > 0 && (
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Next renewals</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {upcoming.map(i => {
              const m = expiryMeta(i.expiry_date);
              return (
                <div key={i.item_id} className={`rounded-md border px-3 py-2 ${m.ring} bg-ink-card`}>
                  <div className="text-[12px] text-text font-medium truncate">{i.display_name}</div>
                  <div className={`text-[11px] font-mono ${m.tone}`}>{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Type chips — generic register, more types appear as they're added */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[{ v: "all", label: "All" }, ...ITEM_TYPES.filter(t => typesPresent.includes(t.v))].map(t => (
          <button key={t.v} type="button" onClick={() => setTypeFilter(t.v)}
            className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
              typeFilter === t.v ? "bg-accent/15 text-accent border-accent/40" : "bg-ink-raised text-text-muted border-line"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : shown.length === 0 ? (
        <Card><EmptyState icon={ShieldCheck} title="Nothing registered yet" description="Add the company's insurance policies — PL, PI, WorkCover, vehicle, tools, cyber." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {shown.map(i => {
            const m = expiryMeta(i.expiry_date);
            const history = historyFor === i.item_id ? historyOf(i.item_id) : [];
            return (
              <Card key={i.item_id} className={`border-l-2 ${m.ring.replace("border-", "border-l-")}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-text">{i.display_name}</span>
                        <Pill label={ITEM_TYPES.find(t => t.v === i.item_type)?.label ?? i.item_type} tone="bid" small />
                      </div>
                      <div className="text-[12px] text-text-muted mt-0.5 flex flex-wrap gap-x-3">
                        {i.provider && <span>{i.provider}</span>}
                        {i.reference && <span className="font-mono">{i.reference}</span>}
                        {i.broker && <span>via {i.broker}</span>}
                      </div>
                    </div>
                    <div className={`text-[12px] font-mono text-right flex-shrink-0 ${m.tone}`}>{m.label}</div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-3 text-[12px]">
                    <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Cover</div>{i.cover_amount ? fmtMoney(Number(i.cover_amount)) : "—"}</div>
                    <div><div className="text-[10px] uppercase tracking-wider text-text-muted">{i.category === "workers_comp" ? "Est. premium" : "Excess"}</div>
                      {i.category === "workers_comp"
                        ? (i.premium_estimate ? fmtMoney(Number(i.premium_estimate)) : "—")
                        : (i.excess_amount ? fmtMoney(Number(i.excess_amount)) : "—")}
                    </div>
                    <div><div className="text-[10px] uppercase tracking-wider text-text-muted">From</div>{i.effective_from ? fmtDate(i.effective_from) : "—"}</div>
                  </div>

                  {/* mig 353 — package-policy section breakdown (sums + excesses per section) */}
                  {(i.cover_sections?.length ?? 0) > 0 && (
                    <div className="mt-3 border border-line rounded-md overflow-hidden">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="bg-ink-raised text-text-muted">
                            <th className="text-left px-2.5 py-1.5 font-medium">Section</th>
                            <th className="text-right px-2.5 py-1.5 font-medium">Sum insured</th>
                            <th className="text-right px-2.5 py-1.5 font-medium">Excess</th>
                          </tr>
                        </thead>
                        <tbody>
                          {i.cover_sections!.map((s, si) => (
                            <tr key={si} className="border-t border-line align-top">
                              <td className="px-2.5 py-1.5 text-text">
                                {s.label}
                                {s.note && <div className="text-[10px] text-text-muted mt-0.5">{s.note}</div>}
                              </td>
                              <td className="px-2.5 py-1.5 text-right font-mono text-text whitespace-nowrap">
                                {s.sum_insured != null ? fmtMoney(Number(s.sum_insured)) : "—"}
                              </td>
                              <td className="px-2.5 py-1.5 text-right font-mono text-text-muted whitespace-nowrap">
                                {s.excess != null ? fmtMoney(Number(s.excess)) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {i.details && <div className="text-[11px] text-text-muted mt-2 line-clamp-2">{i.details}</div>}

                  {/* Documents — CoC + schedule + wording, all under this ONE item */}
                  <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                    {((i as any).docs ?? []).length > 0 ? (
                      ((i as any).docs as any[]).map(d => (
                        <span key={d.doc_id} className="inline-flex items-center rounded-md border border-line overflow-hidden">
                          <button type="button" onClick={() => viewDoc(d.storage_path)}
                            className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] text-text hover:text-accent"
                            title={d.file_name ?? ""}>
                            <FileText size={11} className="text-accent" />
                            {d.doc_type === "certificate_of_currency" ? "CoC"
                              : d.doc_type === "policy_schedule" ? "Schedule"
                              : d.doc_type === "policy_wording" ? "Wording"
                              : d.doc_type === "certificate" ? "Certificate" : "Doc"}
                          </button>
                          <button type="button" onClick={async () => {
                            const { data } = await supabase.storage.from(BUCKET).download(d.storage_path);
                            if (!data) return;
                            const url = URL.createObjectURL(data);
                            const a = document.createElement("a");
                            a.href = url; a.download = d.file_name || "document.pdf";
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                          }} className="px-1.5 py-1.5 border-l border-line text-text-muted hover:text-accent" title="Download">
                            <Download size={11} />
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-[11px] text-warn inline-flex items-center gap-1"><FileText size={11} /> no documents uploaded</span>
                    )}
                    <span className="ml-auto flex items-center gap-1.5">
                      <button type="button" title="Renew — new period, old kept in history"
                        onClick={() => setEditing({ ...i, item_id: undefined, effective_from: "", expiry_date: "", document_path: null, document_name: null, _renewOf: i.item_id } as any)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-line text-[11px] text-accent hover:border-accent"><RefreshCw size={12} /> Renew</button>
                      <button type="button" onClick={() => setEditing(i)}
                        className="text-[11px] text-text-muted hover:text-accent px-1.5">Edit</button>
                      <button type="button" onClick={() => setHistoryFor(h => h === i.item_id ? null : i.item_id)}
                        className="text-text-muted hover:text-accent p-1" title="History"><History size={13} /></button>
                      <button type="button" onClick={() => archive(i)} className="text-text-muted hover:text-danger p-1" title="Archive"><Trash2 size={13} /></button>
                    </span>
                  </div>

                  {historyFor === i.item_id && (
                    <div className="mt-3 border-t border-line pt-2">
                      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">History</div>
                      {history.length === 0 ? (
                        <div className="text-[11px] text-text-muted">No previous periods on record.</div>
                      ) : history.map(h => (
                        <div key={h.item_id} className="text-[11px] text-text-muted flex items-center gap-2 py-0.5 flex-wrap">
                          <span className="font-mono">{h.reference ?? "—"}</span>
                          <span>{h.effective_from ? fmtDate(h.effective_from) : "?"} → {h.expiry_date ? fmtDate(h.expiry_date) : "?"}</span>
                          {h.cover_amount && <span>{fmtMoney(Number(h.cover_amount))}</span>}
                          {h.document_path && (
                            <button type="button" onClick={() => viewDoc(h.document_path!)} className="text-accent hover:underline">cert</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <ItemModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["company_compliance"] }); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Add / Edit / Renew modal. Renew: initial has _renewOf — saving inserts the
// new period then archives + chains the old row.
// -----------------------------------------------------------------------------
function ItemModal({ initial, onClose, onSaved }: { initial: any; onClose: () => void; onSaved: () => void }) {
  const user = useRequireAuth();
  const isEdit = !!initial.item_id;
  const renewOf: string | null = initial._renewOf ?? null;
  const [f, setF] = useState<any>({
    item_type: initial.item_type ?? "insurance",
    category: initial.category ?? "",
    display_name: initial.display_name ?? "",
    provider: initial.provider ?? "",
    broker: initial.broker ?? "",
    reference: initial.reference ?? "",
    cover_amount: initial.cover_amount ?? "",
    excess_amount: initial.excess_amount ?? "",
    premium_estimate: initial.premium_estimate ?? "",
    effective_from: initial.effective_from ?? "",
    expiry_date: initial.expiry_date ?? "",
    details: initial.details ?? "",
  });
  // mig 353 — package-policy sections, editable rows. Carried across on Renew.
  const [sections, setSections] = useState<CoverSection[]>(
    Array.isArray(initial.cover_sections) ? initial.cover_sections : []);
  // Multi-document: CoC + schedule + wording etc, each typed, all on this item.
  const [docFiles, setDocFiles] = useState<{ docType: string; file: File }[]>([]);
  const [nextDocType, setNextDocType] = useState("certificate_of_currency");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const isInsurance = f.item_type === "insurance";
  const DOC_TYPES = [
    { v: "certificate_of_currency", label: "Certificate of Currency" },
    { v: "policy_schedule",         label: "Policy schedule" },
    { v: "policy_wording",          label: "Policy wording" },
    { v: "certificate",             label: "Certificate" },
    { v: "other",                   label: "Other document" },
  ];

  function set(k: string, v: any) { setF((p: any) => ({ ...p, [k]: v })); }

  async function save() {
    if (!f.display_name.trim()) { window.alert("Give it a name — e.g. 'Public Liability — CGU'."); return; }
    setBusy(true);
    try {
      const payload: any = {
        item_type: f.item_type,
        category: f.category || null,
        display_name: f.display_name.trim(),
        provider: f.provider.trim() || null,
        broker: f.broker.trim() || null,
        reference: f.reference.trim() || null,
        cover_amount: f.cover_amount ? Number(f.cover_amount) : null,
        excess_amount: f.excess_amount ? Number(f.excess_amount) : null,
        premium_estimate: f.premium_estimate ? Number(f.premium_estimate) : null,
        effective_from: f.effective_from || null,
        expiry_date: f.expiry_date || null,
        details: f.details.trim() || null,
        // mig 353 — drop empty rows; null (not []) when there are none
        cover_sections: (() => {
          const rows = sections.filter(s => s.label.trim() !== "");
          return rows.length > 0 ? rows.map(s => ({
            label: s.label.trim(),
            sum_insured: s.sum_insured != null && String(s.sum_insured) !== "" ? Number(s.sum_insured) : null,
            excess: s.excess != null && String(s.excess) !== "" ? Number(s.excess) : null,
            note: (s.note ?? "").trim() || null,
          })) : null;
        })(),
        updated_at: new Date().toISOString(),
      };
      let itemId: string = initial.item_id;
      if (isEdit) {
        const { error } = await supabase.from("company_compliance_items").update(payload).eq("item_id", initial.item_id);
        if (error) throw error;
      } else {
        payload.created_by = (user as any)?.user_id ?? null;
        const { data, error } = await supabase.from("company_compliance_items")
          .insert(payload).select("item_id").single();
        if (error) throw error;
        itemId = (data as any).item_id;
        if (renewOf) {
          // Chain: old period archived + points at the new one. History intact.
          await supabase.from("company_compliance_items")
            .update({ is_active: false, superseded_by_item_id: itemId })
            .eq("item_id", renewOf);
        }
      }
      // Upload each typed document + attach to the item.
      for (const d of docFiles) {
        const safe = d.file.name.replace(/[^\w.\-]/g, "_");
        const path = `company-compliance/${f.item_type}/${Date.now()}-${safe}`;
        const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, d.file, { upsert: false });
        if (upErr) { window.alert(`"${d.file.name}" upload failed: ${upErr.message} — item saved, re-add the doc via Edit.`); continue; }
        await supabase.from("company_compliance_documents").insert({
          item_id: itemId, doc_type: d.docType, storage_path: path, file_name: d.file.name,
          uploaded_by: (user as any)?.user_id ?? null,
        });
      }
      onSaved();
    } catch (e: any) {
      window.alert(`Couldn't save: ${e.message ?? e}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line">
          <h3 className="text-lg font-semibold">
            {renewOf ? "Renew — new period" : isEdit ? "Edit item" : "Add compliance item"}
          </h3>
          {renewOf && <p className="text-[12px] text-text-muted mt-1">The current period moves to history automatically.</p>}
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Type">
              <select value={f.item_type} onChange={e => set("item_type", e.target.value)} disabled={isEdit || !!renewOf}
                className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text">
                {ITEM_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            {isInsurance && (
              <Field label="Policy category">
                <select value={f.category} onChange={e => set("category", e.target.value)}
                  className="w-full bg-ink border border-line rounded px-2 py-2 text-[13px] text-text">
                  <option value="">— pick —</option>
                  {INSURANCE_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
              </Field>
            )}
            <Field label="Name"><Input value={f.display_name} onChange={e => set("display_name", e.target.value)} placeholder="Public Liability — CGU" /></Field>
            <Field label={isInsurance ? "Insurer" : "Issuing body"} hint=""><Input value={f.provider} onChange={e => set("provider", e.target.value)} /></Field>
            <Field label="Broker" hint=""><Input value={f.broker} onChange={e => set("broker", e.target.value)} /></Field>
            <Field label={isInsurance ? "Policy #" : "Reference #"} hint=""><Input value={f.reference} onChange={e => set("reference", e.target.value)} /></Field>
            {isInsurance && <Field label="Cover $" hint=""><Input type="number" value={f.cover_amount} onChange={e => set("cover_amount", e.target.value)} /></Field>}
            {isInsurance && f.category !== "workers_comp" && <Field label="Excess $" hint=""><Input type="number" value={f.excess_amount} onChange={e => set("excess_amount", e.target.value)} /></Field>}
            {isInsurance && f.category === "workers_comp" && <Field label="Est. premium $" hint=""><Input type="number" value={f.premium_estimate} onChange={e => set("premium_estimate", e.target.value)} /></Field>}
            <Field label="Effective from" hint=""><Input type="date" value={f.effective_from} onChange={e => set("effective_from", e.target.value)} /></Field>
            <Field label="Expiry / renewal"><Input type="date" value={f.expiry_date} onChange={e => set("expiry_date", e.target.value)} /></Field>
          </div>
          <Field label="Coverage details / notes" hint="">
            <Input value={f.details} onChange={e => set("details", e.target.value)} placeholder="e.g. $20m any one occurrence; Australia-wide incl. telco structures" />
          </Field>
          {/* mig 353 — package-policy sections: sums insured + excesses per section
              (e.g. Zurich Business Package: Property / Burglary / General property / Glass) */}
          {isInsurance && (
            <div className="border border-line rounded-md p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">
                Cover sections <span className="normal-case font-normal">— for package policies; leave empty for single-cover</span>
              </div>
              {sections.map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                  <input value={s.label} placeholder="Section — e.g. General property: portable tools"
                    onChange={e => setSections(list => list.map((x, i2) => i2 === idx ? { ...x, label: e.target.value } : x))}
                    className="col-span-5 bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" />
                  <input type="number" value={s.sum_insured ?? ""} placeholder="Sum $"
                    onChange={e => setSections(list => list.map((x, i2) => i2 === idx ? { ...x, sum_insured: e.target.value === "" ? null : Number(e.target.value) } : x))}
                    className="col-span-2 bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" />
                  <input type="number" value={s.excess ?? ""} placeholder="Excess $"
                    onChange={e => setSections(list => list.map((x, i2) => i2 === idx ? { ...x, excess: e.target.value === "" ? null : Number(e.target.value) } : x))}
                    className="col-span-2 bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" />
                  <input value={s.note ?? ""} placeholder="Note"
                    onChange={e => setSections(list => list.map((x, i2) => i2 === idx ? { ...x, note: e.target.value } : x))}
                    className="col-span-2 bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text" />
                  <button type="button" onClick={() => setSections(list => list.filter((_, i2) => i2 !== idx))}
                    className="col-span-1 text-text-muted hover:text-danger text-[11px] py-1.5" title="Remove section">✕</button>
                </div>
              ))}
              <button type="button"
                onClick={() => setSections(list => [...list, { label: "", sum_insured: null, excess: null, note: null }])}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                <Plus size={13} /> Add section
              </button>
            </div>
          )}
          {/* Documents — add as many as the policy has (CoC, schedule, wording…) */}
          <div className="border border-line rounded-md p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">Documents</div>
            {docFiles.map((d, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[12px]">
                <FileText size={12} className="text-accent" />
                <span className="text-text-muted">{DOC_TYPES.find(t => t.v === d.docType)?.label}:</span>
                <span className="text-text truncate flex-1">{d.file.name}</span>
                <button type="button" onClick={() => setDocFiles(list => list.filter((_, i2) => i2 !== idx))}
                  className="text-text-muted hover:text-danger text-[11px]">remove</button>
              </div>
            ))}
            <div className="flex items-center gap-2 flex-wrap">
              <select value={nextDocType} onChange={e => setNextDocType(e.target.value)}
                className="bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text">
                {DOC_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
              <button type="button" onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                <Upload size={13} /> Pick file
              </button>
              <input ref={fileInput} type="file" accept="application/pdf,image/*" className="hidden"
                onChange={e => {
                  const picked = e.target.files?.[0];
                  if (picked) setDocFiles(list => [...list, { docType: nextDocType, file: picked }]);
                  e.currentTarget.value = "";
                }} />
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={save}>{busy ? "Saving…" : renewOf ? "Save new period" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
